import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import {
  useEquipment,
  useEquipmentMide,
  useUser,
} from "@services/servicesGenericsTables";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

const CountingCommon = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { currentCounting } = useAnalysis();
  const filteredProcedures = useFilteredProcedures("CON");
  // cuando la tabla de relacion equipos analisis este ok, se debe cambiar el uso de equipmentList por equipmentMideListFiltered
  const { /* equipmentMideListFiltered, */ loading } = useEquipmentMide();
  const { equipmentList } = useEquipment();

  const lisentSqpTsieWhite = useMemo(
    () => form.watch("sqp_tsie_white"),
    [form]
  );
  const lisentSqpTsieSample = useMemo(
    () => form.watch("sqp_tsie_sample"),
    [form]
  );
  const countingValidValue = useMemo(
    () => form.watch("counting_valid"),
    [form]
  );

  useEffect(() => {
    const sqpTsieWhite = parseFloat(form.getValues("sqp_tsie_white"));
    const sqpTsieSample = parseFloat(form.getValues("sqp_tsie_sample"));

    if (!isNaN(sqpTsieWhite) && !isNaN(sqpTsieSample)) {
      const variation: any = (
        (sqpTsieSample / sqpTsieWhite) * 100 -
        100
      ).toFixed(2);
      form.setValue("variation_sqp_tsie", variation);
      const appliedCriterion = parseFloat(form.getValues("applied_criterion"));
      if (!isNaN(appliedCriterion)) {
        const lowerBound = 1 - appliedCriterion / 100;
        const upperBound = 1 + appliedCriterion / 100;

        const isValid =
          variation >= lowerBound * 100 - 100 &&
          variation <= upperBound * 100 - 100;
        form.setValue("counting_valid", isValid ? "S" : "N");
      } else {
        form.setValue("counting_valid", "");
      }
    } else {
      form.setValue("counting_valid", "");
    }
  }, [lisentSqpTsieWhite, lisentSqpTsieSample]);

  const stylesInput = useMemo(() => {
    if (countingValidValue === "N") {
      return { backgroundColor: "#ef233c" };
    } else if (countingValidValue === "S") {
      return { backgroundColor: "#a7c957" };
    }
    return {};
  }, [countingValidValue]);

  return (
    <>
      {currentCounting.index !== undefined ? (
        <React.Fragment key={currentCounting.id}>
          <ResponsiveFormContainer columns={5}>
            <MAutocomplete
              key={`counting_analyst-${form.getValues("counting_analyst")}`}
              name="counting_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
              disabled={!currentCounting.canEdit}
            />

            <MAutocomplete
              key={`counting_procedure-${form.getValues("counting_procedure")}`}
              name="counting_procedure"
              options={filteredProcedures}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              disabled={!currentCounting.canEdit}
            />

            <MAutocomplete
              key={`counting_equipment-${form.getValues("counting_equipment")}`}
              name="counting_equipment"
              options={equipmentList}
              label="Seleccione un Equipo"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El equipo es requerido" }}
              loading={loading}
              disabled={!currentCounting.canEdit}
            />
            <MDatePicker
              key="counting_date"
              name="counting_date"
              pickerType="date"
              label="Fecha de contaje"
              validation={{
                required: "La fecha de contaje es requerida",
              }}
              disabled={!currentCounting.canEdit}
            />
          </ResponsiveFormContainer>

          <ResponsiveFormContainer columns={5} mt={2}>
            <MTextField
              name="sqp_tsie_white"
              placeholder="SQP/TSIE Blanco"
              label="SQP/TSIE Blanco"
              type="number"
              validation={{ required: "El SQP/TSIE Blanco es requerido" }}
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="sqp_tsie_sample"
              placeholder="SQP/TSIE Muestra"
              label="SQP/TSIE Muestra"
              type="number"
              validation={{ required: "El SQP/TSIE Muestra es requerido" }}
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="variation_sqp_tsie"
              placeholder="Variación SQP/TSIE (%)"
              label="Variación SQP/TSIE (%)"
              type="number"
              fullWidth
              disabled
            />
            <MTextField
              name="applied_criterion"
              placeholder="Criterio aplicado (%)"
              label="Criterio aplicado (%)"
              type="text"
              fullWidth
              disabled
            />

            <MTextField
              name="counting_valid"
              placeholder="Contaje Válido"
              label="Contaje Válido"
              type="text"
              fullWidth
              disabled
              style={stylesInput}
            />

            <MTextField
              name="gross_white_cpm"
              placeholder="cpm brutas del blanco"
              label="cpm brutas del blanco"
              validation={{ required: "El cpm brutas del blanco es requerido" }}
              type="number"
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="sample_cpm"
              placeholder="cpm muestra"
              label="cpm muestra"
              type="number"
              validation={{ required: "El cpm muestra es requerido" }}
              fullWidth
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="total_white_counting_time"
              placeholder="Tiempo total de contaje del blanco (min)"
              label="Tiempo total de contaje del blanco (min)"
              validation={{
                required: "El tiempo total de contaje del blanco es requerido",
              }}
              type="number"
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="total_sample_counting_time"
              placeholder="Tiempo total de contaje de la muestra (min)"
              label="Tiempo total de contaje de la muestra (min)"
              validation={{
                required:
                  "El tiempo total de contaje de la muestra es requerido",
              }}
              type="number"
              fullWidth
              step="0.01"
              disabled={!currentCounting.canEdit}
            />
          </ResponsiveFormContainer>
        </React.Fragment>
      ) : null}
    </>
  );
};

export default CountingCommon;
